// src/components/QRscanner.tsx
import jsQR, { QRCode } from "jsqr";
import React, { useCallback, useEffect, useRef, useState } from "react";
import QROverlay from "./QROverlay";

interface Props {
  onScanSuccess: (decodedText: string) => void;
  onScanError: (errorMessage: string) => void;
}

/**
 * jsQR 기반 QR 스캐너 (카메라 → <video> → <canvas> → jsQR)
 * - InputMachine.tsx에서 쓰던 props(onScanSuccess/onScanError) 시그니처 유지
 * - 마운트 시 카메라 시작, 언마운트 시 정리 → InputMachine의 조건부 렌더링(qrScanning)과 궁합 OK
 */
const QrScanner: React.FC<Props> = ({ onScanSuccess, onScanError }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }, []);

  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const dw = canvas.width;
    const dh = canvas.height;

    // 현재 프레임을 캔버스에 그리기
    ctx.drawImage(video, 0, 0, dw, dh);

    try {
      const imageData = ctx.getImageData(0, 0, dw, dh);
      const code: QRCode | null = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "attemptBoth", // 조명환경 따라 'attemptBoth' 고려
      });

      if (code && code.data) {
        onScanSuccess(code.data);
        // 성공 시 여기서 종료할지 계속 스캔할지 선택 가능.
        // InputMachine은 성공 직후 컴포넌트를 언마운트(qrScanning=false)하므로 여기선 계속 돌려도 OK.
      }
    } catch (err) {
      // 프레임 단위 오류 스팸 방지: 심각한 오류만 전달
      // onScanError(String(err));
    }

    rafRef.current = requestAnimationFrame(loop);
  }, [onScanSuccess]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) throw new Error("Video/Canvas not ready");

      video.srcObject = stream;
      // iOS Safari 대응: metadata 로드 후 크기 세팅
      await video.play();

      // 비디오 실제 크기 기준 캔버스 크기 설정 (다운스케일하면 성능↑)
      const vw = video.videoWidth || 640;
      const vh = video.videoHeight || 480;
      const scale = 0.5; // 필요 시 0.5~0.75로 낮춰 성능 확보
      canvas.width = Math.max(320, Math.floor(vw * scale));
      canvas.height = Math.max(240, Math.floor(vh * scale));

      setReady(true);
      rafRef.current = requestAnimationFrame(loop);
    } catch (e) {
      stop();
      const msg = e instanceof Error ? e.message : String(e);
      onScanError(`Camera start failed: ${msg}`);
    }
  }, [loop, stop, onScanError]);

  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  return (
    <div style={{ position: "relative", borderRadius: 8, overflow: "hidden" }}>
      {/* 미러링 없이 표시(원본), UI는 캔버스만 보여도 충분 */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        style={{ width: "100%", display: "none" }} // 비디오 숨기고 캔버스만 노출
      />
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          background: "#000",
          opacity: ready ? 1 : 0.6,
          transition: "opacity .2s",
        }}
      />
      {/* 스캔 가이드 라인(선택) */}
      <QROverlay boxWidthPct={64} aspectRatio={1} />

      {/* <div
        style={{
          position: "absolute",
          inset: 0,
          border: "2px dashed rgba(255,255,255,.5)",
          pointerEvents: "none",
        }}
      /> */}
    </div>
  );
};

export default QrScanner;
